export type BrandKey = 'zaveato'

export interface BrandConfig {
  key: BrandKey
  name: string
  tagline: string
  domain: string
  logo: {
    icon: string
    text: string
    accentLetter: string
    accentColor: string  // Tailwind color class for the accent letter
    suffix: string
    fontFamily: string   // CSS font-family for logo
    image?: string       // Path to logo image
    imageNoBorder?: string // Path to logo without border (for white backgrounds)
    favicon?: string     // Path to favicon
  }
  colors: {
    primary: string
    primaryHex: string
    primaryHover: string
    primaryHoverHex: string
    gradient: {
      from: string
      to: string
      fromHex: string
      toHex: string
    }
    accent: string
    accentHex: string
    // Light variants for backgrounds
    primaryLight: string
    primaryLightHex: string
  }
  // Pre-built Tailwind class strings for consistent theming
  classes: {
    // Buttons
    buttonPrimary: string
    buttonSecondary: string
    // Text
    textPrimary: string
    textPrimaryHover: string
    linkPrimary: string
    // Backgrounds
    bgPrimary: string
    bgPrimaryHover: string
    bgGradient: string
    bgLight: string
    // Borders & Focus
    borderPrimary: string
    focusRing: string
    // Form elements
    checkbox: string
    inputFocus: string
    // Tabs/Toggles
    tabActive: string
    tabInactive: string
  }
  features: {
    appointments: boolean
    shop: boolean
  }
  meta: {
    title: string
    description: string
  }
  auth: {
    welcomeMessage: string
    registerMessage: string
  }
  footer: {
    companyName: string
    email: string
  }
  assistant: {
    name: string
  }
  seo: {
    siteName: string
    defaultTitle: string
  }
}

export const brands: Record<BrandKey, BrandConfig> = {
  zaveato: {
    key: 'zaveato',
    name: 'Zaveato',
    // Header tagline removed per user request 2026-08-17. Empty string keeps
    // the type contract intact; consumer layouts should guard render with
    // `v-if="brand.tagline"` so empty doesn't leave an empty span in the DOM.
    tagline: '',
    domain: 'zaveato.co.uk',
    logo: {
      icon: 'pi-bolt',
      // Wordmark split: `text` + `accentLetter` + `suffix` renders as
      // "zav" + "eat" (accent) + "o" + "." (accent) — the double accent
      // spells "eat." — the same play-on-words the mobile app uses.
      // Do NOT change without updating HomeView's hardcoded wordmarks
      // + mobile `ZaveatoLogo` to keep the identity in sync.
      text: 'Zav',
      accentLetter: 'eat',
      accentColor: 'text-rose-500',
      suffix: 'o',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      // image: '/logo.svg',  // Disabled to use text logo with tagline consistently
      favicon: '/favicon.svg'
    },
    colors: {
      primary: 'rose-500',
      primaryHex: '#F43F5E',
      primaryHover: 'rose-600',
      primaryHoverHex: '#E11D48',
      gradient: {
        from: 'rose-500',
        to: 'rose-600',
        fromHex: '#F43F5E',
        toHex: '#E11D48'
      },
      accent: 'slate-900',
      accentHex: '#0F172A',
      primaryLight: 'rose-50',
      primaryLightHex: '#FFF1F2'
    },
    // Pre-built Tailwind class strings for consistent theming
    classes: {
      // Buttons
      buttonPrimary: 'bg-rose-500 hover:bg-rose-600 text-white focus:ring-4 focus:ring-rose-200',
      buttonSecondary: 'bg-white hover:bg-gray-100 text-rose-500 border border-rose-500',
      // Text
      textPrimary: 'text-rose-500',
      textPrimaryHover: 'text-rose-600',
      linkPrimary: 'text-rose-600 hover:text-rose-500',
      // Backgrounds
      bgPrimary: 'bg-rose-500',
      bgPrimaryHover: 'hover:bg-rose-600',
      bgGradient: 'bg-gradient-to-br from-rose-500 via-rose-600 to-rose-700',
      bgLight: 'bg-rose-50',
      // Borders & Focus
      borderPrimary: 'border-rose-500',
      focusRing: 'focus:ring-rose-500 focus:border-rose-500',
      // Form elements
      checkbox: 'rounded border-gray-300 text-rose-600 focus:ring-rose-500',
      inputFocus: 'focus:ring-2 focus:ring-rose-500 focus:border-rose-500',
      // Tabs/Toggles
      tabActive: 'bg-rose-500 text-white shadow-md',
      tabInactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
    },
    features: {
      appointments: true,
      shop: true
    },
    meta: {
      title: 'Zaveato',
      description: 'Discover and order from your favourite local shops, plus book services with trusted UK businesses. Delivery or collection, appointments for healthcare, beauty, driving lessons and more.'
    },
    auth: {
      welcomeMessage: 'Welcome back to Zaveato',
      registerMessage: 'Create your Zaveato account'
    },
    footer: {
      companyName: 'Zaveato',
      email: 'support@zaveato.co.uk'
    },
    assistant: {
      name: 'Zaveato Assistant'
    },
    seo: {
      siteName: 'Zaveato',
      defaultTitle: 'Zaveato — Local Products & Services'
    }
  }
}

export function getBrandByDomain(_hostname: string, _port?: string): BrandConfig {
  // All domains now use Zaveato (unified platform)
  return brands.zaveato
}
